import { useEffect, useState, useMemo } from 'react';
import { Plus, Search, ChevronDown, ChevronRight, Pencil, Trash2, Upload, Loader2, FileDown, FileSpreadsheet, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { adminAPI, categoriesAPI, mainCategoriesAPI, fileUrl } from '@/lib/api';
import { formatPack } from '@/lib/units';
import { toast } from '@/components/ui/sonner';
import { money } from '@/lib/format';

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [collapsed, setCollapsed] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importMode, setImportMode] = useState('append');
  const [importResult, setImportResult] = useState(null);
  const [markupPct, setMarkupPct] = useState(25);
  const [vatRate, setVatRate] = useState(15);
  // Tracks whether the admin has manually overridden the auto-calculated
  // sell price. While false, sell price is recomputed live from cost × markup.
  // The moment the admin types in the Sell Price field, we flip this to true
  // so the manual value is preserved.
  const [priceManuallyEdited, setPriceManuallyEdited] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', price: '', cost_price: '', sku: '',
    main_category_id: '', category_id: '', supplier_id: '', unit_size_value: '', unit_size_unit: '', image_url: '', moq: '', vat_applicable: ''
  });

  const calcSellPrice = (cost, pct) => {
    const c = parseFloat(cost);
    const p = parseFloat(pct);
    if (!isFinite(c) || c <= 0 || !isFinite(p)) return '';
    return money((c * (1 + p / 100)));
  };

  const fetchAll = () => {
    Promise.all([
      adminAPI.listProducts().then(r => setProducts(r.data)),
      adminAPI.listSuppliers().then(r => setSuppliers(r.data)),
      categoriesAPI.list().then(r => setCategories(r.data)),
      mainCategoriesAPI.list().then(r => setMainCategories(r.data)).catch(() => {}),
      adminAPI.getSettings()
        .then(r => {
          const m = parseFloat(r.data?.markup_percentage);
          if (isFinite(m)) setMarkupPct(m);
          const v = parseFloat(r.data?.vat_rate);
          if (isFinite(v)) setVatRate(v);
        })
        .catch(() => {}),
    ]).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const supplierMap = useMemo(() => {
    const m = {};
    suppliers.forEach(s => { m[s.id] = s.name; });
    return m;
  }, [suppliers]);

  const categoryMap = useMemo(() => {
    const m = {};
    categories.forEach(c => { m[c.id] = c.name; });
    return m;
  }, [categories]);

  // Filter and group
  const filtered = useMemo(() => {
    let list = products;
    if (filterSupplier !== 'all') list = list.filter(p => p.supplier_id === filterSupplier);
    if (filterStatus === 'active') list = list.filter(p => p.is_active !== false);
    else if (filterStatus === 'hidden') list = list.filter(p => p.is_active === false);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.sku || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [products, filterSupplier, filterStatus, search]);

  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach(p => {
      const key = p.supplier_id || 'unassigned';
      if (!groups[key]) groups[key] = { name: supplierMap[key] || 'Unassigned', products: [] };
      groups[key].products.push(p);
    });
    return Object.entries(groups).sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [filtered, supplierMap]);

  const toggleCollapse = (key) => {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const openCreate = () => {
    setEditing(null);
    setPriceManuallyEdited(false);
    setForm({ name: '', description: '', price: '', cost_price: '', sku: '', main_category_id: '', category_id: '', supplier_id: '', unit_size_value: '', unit_size_unit: '', image_url: '', moq: '', pack_qty: '', vat_applicable: '' });
    setDialogOpen(true);
  };

  const openEdit = (product) => {
    setEditing(product);
    // The form's "Sell Price" is the manual OVERRIDE, not the computed display
    // price. If the product has no override set, the field is blank and the
    // hint shows the auto-calc formula. As soon as the admin types or clicks
    // Recalc, the override is set.
    const override = product.price_override;
    const hasOverride = override !== null && override !== undefined && override > 0;
    setPriceManuallyEdited(hasOverride);
    setForm({
      name: product.name, description: product.description || '',
      price: hasOverride ? String(override) : '',
      cost_price: String(product.cost_price || ''),
      sku: product.sku || '',
      main_category_id: product.main_category_id || (categories.find(c => c.id === product.category_id)?.main_category_id) || '',
      category_id: product.category_id || '',
      supplier_id: product.supplier_id || '',
      unit_size_value: String(product.unit_size_value || ''),
      unit_size_unit: product.unit_size_unit || '',
      image_url: product.image_url || '',
      moq: product.moq ? String(product.moq) : '',
      pack_qty: product.pack_qty && product.pack_qty > 1 ? String(product.pack_qty) : '',
      vat_applicable: product.vat_override ? (product.vat_applicable ? 'true' : 'false') : ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        cost_price: parseFloat(form.cost_price) || 0,
        unit_size_value: form.unit_size_value ? parseFloat(form.unit_size_value) : null,
        moq: form.moq ? Math.max(0, parseInt(form.moq, 10)) : 0,
        pack_qty: form.pack_qty ? Math.max(1, parseInt(form.pack_qty, 10)) : 1,
      };
      // Sell price semantics:
      //  - If the admin entered/recalc'd a positive number, send it as the override.
      //  - If the field is blank, send price=0 to tell the backend to CLEAR
      //    any existing override and revert to auto-calc from markup.
      const sell = parseFloat(form.price);
      payload.price = isFinite(sell) && sell > 0 ? sell : 0;
      // VAT override: '' = follow supplier (omit on create / null on edit to reset);
      // 'true'/'false' = explicit per-product override.
      if (form.vat_applicable === 'true') payload.vat_applicable = true;
      else if (form.vat_applicable === 'false') payload.vat_applicable = false;
      else payload.vat_applicable = editing ? null : undefined;
      if (editing) {
        await adminAPI.updateProduct(editing.id, payload);
        toast.success('Product updated');
      } else {
        await adminAPI.createProduct(payload);
        toast.success('Product created');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminAPI.uploadImage(file);
      setForm(f => ({ ...f, image_url: res.data.url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    let inCarts = 0;
    try { inCarts = (await adminAPI.productCartUsage(id)).data?.cart_count || 0; } catch { /* non-blocking */ }
    const warn = inCarts > 0
      ? `This product is in ${inCarts} active customer cart${inCarts > 1 ? 's' : ''}. Deleting it will require ${inCarts > 1 ? 'those customers' : 'that customer'} to remove it before checking out.\n\nDelete anyway?`
      : 'Delete this product?';
    if (!window.confirm(warn)) return;
    try { await adminAPI.deleteProduct(id); toast.success('Deleted'); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  const handleToggleActive = async (product, e) => {
    if (e) e.stopPropagation();
    const next = !(product.is_active !== false); // hidden -> active, active -> hidden
    if (!next) {
      let inCarts = 0;
      try { inCarts = (await adminAPI.productCartUsage(product.id)).data?.cart_count || 0; } catch { /* non-blocking */ }
      if (inCarts > 0 && !window.confirm(`"${product.name}" is in ${inCarts} active customer cart${inCarts > 1 ? 's' : ''}. Hiding it will require ${inCarts > 1 ? 'those customers' : 'that customer'} to remove it before checking out.\n\nHide anyway?`)) return;
    }
    // Optimistic update so the switch responds instantly.
    setProducts(prev => prev.map(p => (p.id === product.id ? { ...p, is_active: next } : p)));
    try {
      await adminAPI.updateProduct(product.id, { is_active: next });
      toast.success(next ? `"${product.name}" is now visible on the storefront` : `"${product.name}" hidden from the storefront`);
    } catch {
      // Roll back on failure.
      setProducts(prev => prev.map(p => (p.id === product.id ? { ...p, is_active: !next } : p)));
      toast.error('Could not update visibility');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminAPI.bulkImportProducts(file, importMode);
      const r = res.data || {};
      const imported = r.imported ?? 0;
      const updated = r.updated ?? 0;
      const skipped = r.skipped ?? 0;
      const errCount = (r.errors || []).length;
      setImportResult(r);
      const summary = [imported && `${imported} added`, updated && `${updated} updated`].filter(Boolean).join(' · ');
      if ((imported + updated) > 0 && errCount === 0) {
        toast.success(`Import complete: ${summary || 'no changes'}`);
      } else if ((imported + updated) > 0) {
        toast.warning(`${summary} · Skipped ${skipped}. Click "View details" for errors.`);
      } else {
        toast.error(`No products imported (${skipped} skipped). Click "View details" to see why.`);
      }
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      toast.error(`Import failed: ${detail}`);
    } finally { setUploading(false); e.target.value = ''; }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await adminAPI.downloadImportTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'avz-products-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Failed to download template'); }
  };

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await adminAPI.exportProducts(filterSupplier);
      const supplierName = filterSupplier !== 'all'
        ? (supplierMap[filterSupplier] || 'Supplier').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
        : 'All';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `AVZ_Products_${supplierName}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(filterSupplier !== 'all'
        ? `Exported ${supplierMap[filterSupplier] || 'supplier'} products`
        : 'All products exported');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-light text-foreground tracking-tight">Products</h1>
          <p className="font-body text-sm text-foreground/70 mt-1">{products.length} products</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={handleExport}
            disabled={exporting}
            data-testid="export-products-btn"
            variant="outline"
            size="sm"
            className="rounded-full border-gold/40 text-gold hover:bg-gold/10 font-body text-xs gap-1.5"
          >
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
            {filterSupplier !== 'all' ? `Export ${supplierMap[filterSupplier] || 'Supplier'}` : 'Export All'}
          </Button>
          <Button
            onClick={handleDownloadTemplate}
            data-testid="download-product-template-btn"
            variant="outline"
            size="sm"
            className="rounded-full border-border text-foreground/70 hover:text-foreground font-body text-xs gap-1.5"
          >
            <FileDown className="h-3.5 w-3.5" /> Template
          </Button>
          <Select value={importMode} onValueChange={setImportMode}>
            <SelectTrigger className="w-28 rounded-xl bg-card border-border font-body text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="append">Add &amp; Update</SelectItem>
              <SelectItem value="replace">Replace</SelectItem>
            </SelectContent>
          </Select>
          <label className="cursor-pointer">
            <input type="file" accept=".xlsx,.xls" onChange={handleBulkUpload} className="hidden" />
            <Button as="span" variant="outline" className="rounded-full border-teal/30 text-teal font-body text-xs gap-1.5 pointer-events-none" size="sm" disabled={uploading}>
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Import
            </Button>
          </label>
          <Button onClick={openCreate} data-testid="add-product-btn" className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm gap-2">
            <Plus className="h-4 w-4" /> Add Product
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
          <Input
            data-testid="product-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search products by name, SKU..."
            className="pl-10 rounded-xl bg-card border-border font-body text-sm"
          />
        </div>
        <Select value={filterSupplier} onValueChange={setFilterSupplier}>
          <SelectTrigger data-testid="product-supplier-filter" className="w-56 rounded-xl bg-card border-border font-body text-sm">
            <SelectValue placeholder="All Suppliers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Suppliers</SelectItem>
            {[...suppliers].sort((a,b)=>(a.name||'').localeCompare(b.name||'',undefined,{sensitivity:'base'})).map(s => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger data-testid="product-status-filter" className="w-40 rounded-xl bg-card border-border font-body text-sm">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="hidden">Hidden</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3,4].map(i => <div key={i} className="h-16 rounded-2xl bg-muted animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-muted/30 border border-border/50">
          <p className="font-heading text-xl text-foreground/50">No products found</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="products-grouped">
          {grouped.map(([supplierId, group]) => (
            <div key={supplierId} className="rounded-2xl border border-border overflow-hidden" data-testid={`supplier-group-${supplierId}`}>
              {/* Supplier header */}
              <button
                onClick={() => toggleCollapse(supplierId)}
                className="w-full flex items-center justify-between p-4 bg-teal/10 hover:bg-teal/15 transition-colors"
                data-testid={`collapse-toggle-${supplierId}`}
              >
                <div className="flex items-center gap-3">
                  {collapsed[supplierId] ? <ChevronRight className="h-5 w-5 text-teal" /> : <ChevronDown className="h-5 w-5 text-teal" />}
                  <h3 className="font-heading text-lg font-medium text-foreground">{group.name}</h3>
                  <span className="font-body text-xs text-foreground/50 bg-muted px-2 py-0.5 rounded-full">
                    {group.products.length} products
                  </span>
                </div>
              </button>

              {/* Products */}
              {!collapsed[supplierId] && (
                <div>
                  {/* Table header */}
                  <div className="hidden sm:grid grid-cols-[1fr_80px_88px_56px_52px_78px_70px_72px_48px] gap-3 px-4 py-2 bg-muted/40 text-foreground/50 font-body text-xs uppercase tracking-wider">
                    <span>Product</span>
                    <span>SKU</span>
                    <span>Unit Size</span>
                    <span className="text-center">Pack</span>
                    <span className="text-center">MOQ</span>
                    <span className="text-right">Cost</span>
                    <span className="text-right">Price</span>
                    <span className="text-center">Status</span>
                    <span></span>
                  </div>
                  {group.products.map(product => (
                    <div
                      key={product.id}
                      data-testid={`product-row-admin-${product.id}`}
                      className={`grid grid-cols-1 sm:grid-cols-[1fr_80px_88px_56px_52px_78px_70px_72px_48px] gap-1 sm:gap-3 items-center px-4 py-3 border-t border-border/50 hover:bg-muted/20 cursor-pointer transition-colors group ${product.is_active === false ? 'opacity-60' : ''}`}
                      onClick={() => openEdit(product)}
                    >
                      <div className="min-w-0 flex items-center gap-2.5">
                        <div className="h-9 w-9 rounded-lg bg-muted border border-border overflow-hidden flex items-center justify-center shrink-0" data-testid={`product-thumb-${product.id}`}>
                          {product.image_url
                            ? <img src={fileUrl(product.image_url, 128)} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            : <ImageIcon className="h-4 w-4 text-foreground/25" data-testid={`product-no-image-${product.id}`} />}
                        </div>
                        <div className="min-w-0">
                          <p className="font-body text-sm font-medium text-foreground group-hover:text-teal transition-colors truncate">
                            {product.name}
                            {product.is_active === false && (
                              <span className="ml-2 align-middle inline-block rounded-full bg-red-900/30 text-red-300 border border-red-700/40 px-1.5 py-0.5 text-[10px] font-body uppercase tracking-wide" data-testid={`product-hidden-badge-${product.id}`}>
                                Hidden
                              </span>
                            )}
                          </p>
                          <p className="font-body text-xs text-foreground/40 truncate">{[product.main_category_name, categoryMap[product.category_id]].filter(Boolean).join(' › ')}</p>
                        </div>
                      </div>
                      <span className="font-body text-xs text-foreground/60 font-mono">{product.sku || '-'}</span>
                      <span className="font-body text-xs text-teal font-semibold">
                        {formatPack(product) || '-'}
                      </span>
                      <span className="font-body text-xs text-foreground/70 text-center" data-testid={`product-pack-qty-${product.id}`}>
                        {product.pack_qty && product.pack_qty > 1 ? `${product.pack_qty}` : '1'}
                      </span>
                      <span className="font-body text-xs text-foreground/70 text-center" data-testid={`product-moq-${product.id}`}>
                        {product.moq && product.moq > 0 ? product.moq : '-'}
                      </span>
                      <span className="font-body text-xs text-foreground/50 text-right" data-testid={`product-cost-${product.id}`}>
                        R{money(product.cost_price) || '-'}
                        <span
                          className={`block text-[10px] ${product.vat_applicable ? 'text-teal/70' : 'text-foreground/35'}`}
                          data-testid={`product-exvat-${product.id}`}
                        >
                          ex R{money(product.cost_price_ex_vat ?? product.cost_price)}
                        </span>
                      </span>
                      <span className="font-body text-sm font-semibold text-foreground text-right">R{money(product.price)}</span>
                      <div className="flex justify-center" onClick={e => e.stopPropagation()}>
                        <Switch
                          data-testid={`product-active-toggle-${product.id}`}
                          checked={product.is_active !== false}
                          onCheckedChange={() => handleToggleActive(product)}
                          className="data-[state=checked]:bg-teal"
                          aria-label={product.is_active === false ? 'Show on storefront' : 'Hide from storefront'}
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-900/20" onClick={e => handleDelete(product.id, e)}>
                          <Trash2 className="h-3 w-3 text-red-400/60" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-background rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">
              {editing ? 'Edit Product' : 'Add Product'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="font-body text-sm text-foreground/70 font-medium">Name *</Label>
              <Input data-testid="product-name-input" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" />
            </div>
            <div>
              <Label className="font-body text-sm text-foreground/70 font-medium">Description</Label>
              <Input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" />
            </div>
            <div>
              <Label className="font-body text-sm text-foreground/70 font-medium">Product Image</Label>
              <div className="flex items-center gap-3 mt-1.5">
                <div className="h-16 w-16 rounded-xl bg-muted border border-border overflow-hidden flex items-center justify-center shrink-0">
                  {form.image_url
                    ? <img src={fileUrl(form.image_url)} alt="Product" className="h-full w-full object-cover" data-testid="product-image-preview" />
                    : <ImageIcon className="h-5 w-5 text-foreground/30" />}
                </div>
                <input type="file" accept="image/*" id="product-image-input" className="hidden" onChange={handleImageUpload} data-testid="product-image-input" />
                <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => document.getElementById('product-image-input').click()} className="rounded-full font-body text-xs gap-1.5" data-testid="product-image-upload-btn">
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  {form.image_url ? 'Replace' : 'Upload'}
                </Button>
                {form.image_url && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setForm(f => ({...f, image_url: ''}))} className="rounded-full font-body text-xs text-red-400">Remove</Button>
                )}
              </div>
              <p className="font-body text-[11px] text-foreground/40 mt-1.5">Auto-compressed to WebP. Shown on the storefront product detail.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-body text-sm text-foreground/70 font-medium">SKU</Label>
                <Input value={form.sku} onChange={e => setForm(f => ({...f, sku: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 font-medium">Supplier</Label>
                <Select value={form.supplier_id} onValueChange={v => setForm(f => ({...f, supplier_id: v}))}>
                  <SelectTrigger className="rounded-xl bg-muted border-border mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{[...suppliers].sort((a,b)=>(a.name||'').localeCompare(b.name||'',undefined,{sensitivity:'base'})).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-body text-sm text-foreground/70 font-medium">Main Category</Label>
                <Select
                  value={form.main_category_id}
                  onValueChange={v => setForm(f => ({
                    ...f,
                    main_category_id: v,
                    // Clear the sub-category if it no longer belongs to the chosen main.
                    category_id: categories.find(c => c.id === f.category_id)?.main_category_id === v ? f.category_id : ''
                  }))}
                >
                  <SelectTrigger data-testid="product-main-category-select" className="rounded-xl bg-muted border-border mt-1.5"><SelectValue placeholder="Select main category" /></SelectTrigger>
                  <SelectContent>{[...mainCategories].sort((a,b)=>(a.name||'').localeCompare(b.name||'',undefined,{sensitivity:'base'})).map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 font-medium">Category</Label>
                <Select value={form.category_id} onValueChange={v => setForm(f => ({...f, category_id: v}))} disabled={!form.main_category_id}>
                  <SelectTrigger data-testid="product-category-select" className="rounded-xl bg-muted border-border mt-1.5"><SelectValue placeholder={form.main_category_id ? 'Select category' : 'Pick a main category first'} /></SelectTrigger>
                  <SelectContent>{[...categories].filter(c => c.main_category_id === form.main_category_id).sort((a,b)=>(a.name||'').localeCompare(b.name||'',undefined,{sensitivity:'base'})).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-body text-sm text-foreground/70 font-medium">Cost Price (ZAR)</Label>
                <Input
                  data-testid="product-cost-input"
                  type="number" step="0.01" value={form.cost_price}
                  onChange={e => {
                    const cost = e.target.value;
                    setForm(f => {
                      const next = { ...f, cost_price: cost };
                      // Auto-fill sell price from markup unless admin overrode it
                      if (!priceManuallyEdited) {
                        next.price = calcSellPrice(cost, markupPct);
                      }
                      return next;
                    });
                  }}
                  className="rounded-xl bg-muted border-border mt-1.5"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="font-body text-sm text-foreground/70 font-medium">Sell Price (ZAR)</Label>
                  <button
                    type="button"
                    data-testid="recalc-price-btn"
                    onClick={() => {
                      const recalc = calcSellPrice(form.cost_price, markupPct);
                      if (!recalc) {
                        toast.error('Enter a cost price first');
                        return;
                      }
                      setForm(f => ({ ...f, price: recalc }));
                      setPriceManuallyEdited(false);
                      toast.success(`Recalculated at ${markupPct}% markup`);
                    }}
                    className="font-body text-[10px] uppercase tracking-wider text-teal hover:text-teal-dark transition-colors"
                  >
                    ↻ Recalc
                  </button>
                </div>
                <Input
                  data-testid="product-price-input"
                  type="number" step="0.01" value={form.price}
                  onChange={e => {
                    setForm(f => ({ ...f, price: e.target.value }));
                    setPriceManuallyEdited(true);
                  }}
                  className="rounded-xl bg-muted border-border mt-1.5"
                />
                <p className="font-body text-[11px] text-foreground/40 mt-1" data-testid="markup-hint">
                  {priceManuallyEdited
                    ? `Manual override · saved with this product. Clear field to revert to auto.`
                    : `Auto: cost × (1 + ${markupPct}%) — leave blank to keep auto.`}
                </p>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-muted/40 p-3 space-y-2" data-testid="product-vat-fieldset">
              <div className="flex items-center justify-between gap-3">
                <Label className="font-body text-sm text-foreground/70 font-medium">VAT status</Label>
                <select
                  data-testid="product-vat-select"
                  value={form.vat_applicable}
                  onChange={e => setForm(f => ({ ...f, vat_applicable: e.target.value }))}
                  className="rounded-xl bg-card border border-border px-3 py-2 font-body text-sm text-foreground"
                >
                  <option value="">Follow supplier default{suppliers.find(s => s.id === form.supplier_id)?.is_vat_vendor ? ' (VATable)' : ' (Not VATable)'}</option>
                  <option value="true">VATable (override)</option>
                  <option value="false">Not VATable (override)</option>
                </select>
              </div>
              {(() => {
                const sel = suppliers.find(s => s.id === form.supplier_id);
                const effVat = form.vat_applicable === 'true' ? true : form.vat_applicable === 'false' ? false : !!sel?.is_vat_vendor;
                const c = parseFloat(form.cost_price);
                const ex = (isFinite(c) && c > 0) ? (effVat && vatRate > 0 ? c / (1 + vatRate / 100) : c) : null;
                return (
                  <p className="font-body text-[11px] text-foreground/50" data-testid="product-exvat-preview">
                    {ex === null
                      ? `Enter a cost price to see the VAT-exclusive value.`
                      : effVat
                        ? `VATable @ ${vatRate}% → Cost Ex VAT: R${money(ex)}`
                        : `Not VATable → Cost Ex VAT equals cost: R${money(ex)}`}
                  </p>
                );
              })()}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-body text-sm text-foreground/70 font-medium">Unit Size Value</Label>
                <Input type="number" value={form.unit_size_value} onChange={e => setForm(f => ({...f, unit_size_value: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" placeholder="100" />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 font-medium">Unit Size Unit</Label>
                <Select value={form.unit_size_unit} onValueChange={v => setForm(f => ({...f, unit_size_unit: v}))}>
                  <SelectTrigger className="rounded-xl bg-muted border-border mt-1.5"><SelectValue placeholder="Unit" /></SelectTrigger>
                  <SelectContent>
                    {['ml', 'L', 'g', 'kg', 'mg', 'oz', 'lb', 'pcs', 'each', 'pack', 'capsules', 'tablets', 'sachets'].map(u => <SelectItem key={u} value={u} data-testid={`unit-option-${u}`}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="font-body text-sm text-foreground/70 font-medium">Units per Pack</Label>
              <Input
                type="number" min="1" step="1"
                data-testid="product-pack-qty-input"
                value={form.pack_qty}
                onChange={e => setForm(f => ({ ...f, pack_qty: e.target.value }))}
                className="rounded-xl bg-muted border-border mt-1.5"
                placeholder="1"
              />
              <p className="font-body text-xs text-foreground/40 mt-1">
                How many inner units make up one orderable pack (e.g. a case of 6). Leave blank or 1 for a single unit. The Unit Size above is the size of ONE inner unit.
              </p>
            </div>
            <div>
              <Label className="font-body text-sm text-foreground/70 font-medium">Minimum Order Quantity (MOQ)</Label>
              <Input
                type="number" min="0" step="1"
                data-testid="product-moq-input"
                value={form.moq}
                onChange={e => setForm(f => ({ ...f, moq: e.target.value }))}
                className="rounded-xl bg-muted border-border mt-1.5"
                placeholder="0"
              />
              <p className="font-body text-xs text-foreground/40 mt-1">
                Customers must order at least this many units of this product. Leave blank or 0 for no minimum.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full border-border font-body text-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="product-save-btn" className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import results dialog */}
      <Dialog open={!!importResult} onOpenChange={(o) => { if (!o) setImportResult(null); }}>
        <DialogContent className="max-w-2xl bg-card border-border max-h-[80vh] overflow-hidden flex flex-col" data-testid="import-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">Import results</DialogTitle>
          </DialogHeader>
          {importResult && (
            <div className="flex flex-col gap-4 overflow-hidden">
              <div className="grid grid-cols-4 gap-3 text-center">
                <div className="rounded-xl bg-muted/40 border border-border p-3">
                  <div className="font-heading text-2xl text-teal" data-testid="import-imported-count">{importResult.imported ?? 0}</div>
                  <div className="font-body text-xs uppercase tracking-wider text-foreground/60 mt-1">Added</div>
                </div>
                <div className="rounded-xl bg-muted/40 border border-border p-3">
                  <div className="font-heading text-2xl text-gold" data-testid="import-updated-count">{importResult.updated ?? 0}</div>
                  <div className="font-body text-xs uppercase tracking-wider text-foreground/60 mt-1">Updated</div>
                </div>
                <div className="rounded-xl bg-muted/40 border border-border p-3">
                  <div className="font-heading text-2xl text-foreground/80" data-testid="import-skipped-count">{importResult.skipped ?? 0}</div>
                  <div className="font-body text-xs uppercase tracking-wider text-foreground/60 mt-1">Skipped</div>
                </div>
                <div className="rounded-xl bg-muted/40 border border-border p-3">
                  <div className="font-heading text-2xl text-rose-400" data-testid="import-errors-count">{(importResult.errors || []).length}</div>
                  <div className="font-body text-xs uppercase tracking-wider text-foreground/60 mt-1">Errors</div>
                </div>
              </div>
              {importResult.mode === 'replace' && (importResult.deleted ?? 0) > 0 && (
                <p className="font-body text-xs text-foreground/60">
                  Replace mode: removed {importResult.deleted} existing product{importResult.deleted === 1 ? '' : 's'} from the referenced suppliers before import.
                </p>
              )}
              {(importResult.created_main_categories || []).length > 0 && (
                <p className="font-body text-xs text-foreground/60">
                  Auto-created main categories: {importResult.created_main_categories.join(', ')}
                </p>
              )}
              {(importResult.created_categories || []).length > 0 && (
                <p className="font-body text-xs text-foreground/60">
                  Auto-created categories: {importResult.created_categories.join(', ')}
                </p>
              )}
              {(importResult.errors || []).length > 0 && (
                <div className="flex-1 overflow-y-auto rounded-xl border border-border" data-testid="import-error-list">
                  <table className="w-full">
                    <thead className="bg-muted/40 sticky top-0">
                      <tr>
                        <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 p-2 w-16">Row</th>
                        <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 p-2">Product</th>
                        <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 p-2">Issue(s)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.errors.map((err, i) => (
                        <tr key={i} className="border-t border-border/50">
                          <td className="font-body text-xs text-foreground/60 p-2 align-top">{err.row}</td>
                          <td className="font-body text-xs text-foreground p-2 align-top">{err.name || '—'}</td>
                          <td className="font-body text-xs text-rose-300 p-2 align-top">
                            <ul className="list-disc list-inside space-y-0.5">
                              {(err.issues || []).map((iss, j) => <li key={j}>{iss}</li>)}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportResult(null)} data-testid="import-result-close-btn" className="rounded-full border-border font-body text-sm">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
